import React, { useState, useEffect } from "react";
import { Fan, Lightbulb, Radio, Bell, Power, Camera, Volume2, VolumeX, ShieldOff, Thermometer, Wind, Monitor } from "lucide-react";
import { useApp } from "../../context/AppContext";
import { Panel, Overline } from "../common/Panel";

const tempColor = (t) => (t >= 65 ? "var(--danger)" : t >= 50 ? "var(--warning)" : "var(--accent)");
const tempLabel = (t) => (t >= 65 ? "CRITICAL" : t >= 50 ? "HOT" : t >= 35 ? "WARM" : "NORMAL");
const gasColor  = (q) => (q === "DANGER" ? "var(--danger)" : q === "POOR" ? "var(--warning)" : "var(--accent)");

const LCD_PAGES = [
  ["L1",    "1", "L1 Power"],
  ["L2",    "2", "L2 Power"],
  ["L3",    "3", "L3 Power"],
  ["TOTAL", "4", "Totals"],
  ["GAS",   "5", "Gas Quality"],
];

const IR_KEYS = [
  ["POWER",   "Toggle system standby"],
  ["▲ / ▼",   "Fan speed up / down"],
  ["FUNC",    "Cycle fan AUTO mode"],
  ["◀ / ▶",   "Enclosure light on / off"],
  ["EQ",      "Toggle light hub"],
  ["0 – 5",   "Buzzer beep count (0 = stop)"],
  ["ST/REPT", "Trigger camera snapshot"],
  ["U/SD",    "Mute / unmute alerts"],
];

function Btn({ active, onClick, children, danger, disabled, testid, className = "" }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className={`px-4 py-2.5 rounded-md font-mono text-sm font-medium border transition-colors duration-200 active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed ${
        active
          ? "bg-[var(--accent)] text-black border-[var(--accent)]"
          : danger
          ? "border-[var(--danger)]/40 text-[var(--danger)] hover:bg-[var(--danger)]/10"
          : "bg-[var(--surface2)] border-[var(--bd)] text-[var(--t1)] hover:border-[var(--bd-strong)]"
      } ${className}`}
    >
      {children}
    </button>
  );
}

function Group({ icon: Icon, title, badge, children }) {
  return (
    <Panel className="p-5">
      <div className="flex items-center gap-2 mb-4">
        <Icon size={16} className="text-[var(--accent)]" />
        <Overline>{title}</Overline>
        {badge && <span className="ml-auto text-[10px] font-mono px-2 py-0.5 rounded-full bg-[var(--accent-soft)] text-[var(--accent)]">{badge}</span>}
      </div>
      <div className="flex flex-wrap gap-2">{children}</div>
    </Panel>
  );
}

export default function Controls() {
  const { sendCommand, sendCamCommand, telemetry, mqttConnected } = useApp();

  // Local UI state - synced from telemetry when available
  const [fan,      setFan]      = useState(null);
  const [light,    setLight]    = useState(false);
  const [ir,       setIr]       = useState(false);
  const [muted,    setMuted]    = useState(false);
  const [standby,  setStandby]  = useState(false);
  const [lcdPage,  setLcdPage]  = useState(null);

  // Sync from live telemetry
  useEffect(() => {
    if (!telemetry) return;
    if (telemetry.fanAuto)         setFan("auto");
    else if (telemetry.fan != null) setFan(telemetry.fan === 0 ? "off" : String(telemetry.fan));
    if (telemetry.light    != null) setLight(!!telemetry.light);
    if (telemetry.ir       != null) setIr(!!telemetry.ir);
    if (telemetry.mute     != null) setMuted(!!telemetry.mute);
    if (telemetry.standby  != null) setStandby(!!telemetry.standby);
  }, [telemetry]);

  const fanOpts = [
    ["OFF", "off"], ["30%", "30"], ["60%", "60"], ["100%", "100"], ["AUTO", "auto"],
  ];

  const cmd = (device, action, label) => {
    sendCommand(device, action, label);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <Overline>Device Commands</Overline>
        <h2 className="font-heading font-bold text-2xl tracking-tight mt-1">Remote Control</h2>
        <p className="text-sm text-[var(--t2)] mt-1">
          Commands publish to{" "}
          <span className="font-mono text-[var(--accent)]">campus/energy/cmd</span> via HiveMQ.{" "}
          {!mqttConnected && <span className="text-[var(--danger)] font-mono">MQTT OFFLINE</span>}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

        {/* System Standby */}
        <Group icon={Power} title="System Standby" badge={standby ? "STANDBY" : "ACTIVE"}>
          <Btn
            active={!standby}
            testid="standby-off"
            onClick={() => { setStandby(false); cmd("system", "wake", "System Wake"); }}
          >
            WAKE
          </Btn>
          <Btn
            active={standby}
            testid="standby-on"
            onClick={() => { setStandby(true); cmd("system", "standby", "System Standby"); }}
          >
            STANDBY
          </Btn>
        </Group>

        {/* Fan */}
        <Group icon={Fan} title="Fan Speed" badge={fan ? `${fan === "auto" ? "AUTO" : fan === "off" ? "OFF" : fan + "%"}` : "-"}>
          {fanOpts.map(([label, val]) => (
            <Btn
              key={val}
              active={fan === val}
              testid={`fan-${val}`}
              onClick={() => { setFan(val); cmd("fan", val, `Fan ${label}`); }}
            >
              {label}
            </Btn>
          ))}
        </Group>

        {/* Temperature Sensor */}
        <Group icon={Thermometer} title="Temperature Sensor" badge={telemetry?.temperature != null ? `${telemetry.temperature}°C` : "-"}>
          {telemetry?.temperature != null && (
            <div className="w-full mb-2 flex items-center gap-3">
              <span className="text-3xl font-mono font-bold" style={{ color: tempColor(telemetry.temperature) }}>
                {telemetry.temperature}°C
              </span>
              <span className="text-xs font-mono px-2 py-1 rounded-full border" style={{ color: tempColor(telemetry.temperature), borderColor: tempColor(telemetry.temperature) + "44" }}>
                {tempLabel(telemetry.temperature)}
              </span>
            </div>
          )}
          <Btn active={fan === "auto"} testid="temp-fan-auto" onClick={() => { setFan("auto"); cmd("fan", "auto", "Fan AUTO (thermal)"); }}>
            <Fan size={13} className="inline mr-1" /> FAN AUTO
          </Btn>
          <Btn active={fan === "off"} testid="temp-fan-off" onClick={() => { setFan("off"); cmd("fan", "off", "Fan OFF"); }}>
            FAN OFF
          </Btn>
          <p className="w-full text-xs text-[var(--t2)] font-mono mt-1">DS18B20 · GPIO 4 - fan ramps automatically in AUTO mode.</p>
        </Group>

        {/* Gas Sensor (MQ-135) */}
        <Group icon={Wind} title="Gas Sensor (MQ-135)" badge={telemetry?.gas_quality ?? "-"}>
          {telemetry?.gas_quality && (
            <div className="w-full mb-2 flex items-center gap-3">
              <span className="text-2xl font-mono font-bold" style={{ color: gasColor(telemetry.gas_quality) }}>
                {telemetry.gas_quality}
              </span>
              {telemetry.gas_ppm != null && (
                <span className="text-sm font-mono text-[var(--t2)]">{Math.round(telemetry.gas_ppm)} ppm</span>
              )}
            </div>
          )}
          <Btn testid="gas-buzz-test" onClick={() => cmd("buzzer", "1", "Buzzer ×1 (gas test)")}>
            BUZZ TEST
          </Btn>
          <Btn testid="gas-buzz-alert" danger onClick={() => cmd("buzzer", "3", "Buzzer ×3 (gas alert)")}>
            <Bell size={13} className="inline mr-1" /> ALERT ×3
          </Btn>
          <Btn testid="gas-buzz-stop" onClick={() => cmd("buzzer", "0", "Buzzer STOP")}>
            SILENCE
          </Btn>
          <p className="w-full text-xs text-[var(--t2)] font-mono mt-1">MQ-135 · GPIO 34 (ADC) - buzzer triggers on DANGER.</p>
        </Group>

        {/* LCD Display (20×4) */}
        <Group icon={Monitor} title="LCD Display (20×4)" badge="I²C">
          {LCD_PAGES.map(([label, val, desc]) => (
            <Btn key={val} active={lcdPage === val} testid={`lcd-page-${val}`} onClick={() => { setLcdPage(val); cmd("lcd", `page_${val}`, `LCD → ${desc}`); }}>
              {label}
            </Btn>
          ))}
          <p className="w-full text-xs text-[var(--t2)] font-mono mt-1">Jump to page · auto-cycles every 5 s · SDA GPIO 21 · SCL GPIO 22.</p>
        </Group>

        {/* Enclosure Light */}
        <Group icon={Lightbulb} title="Enclosure Light" badge={light ? "ON" : "OFF"}>
          <Btn active={light}  testid="light-on"  onClick={() => { setLight(true);  cmd("light", "on",  "Light ON");  }}>ON</Btn>
          <Btn active={!light} testid="light-off" onClick={() => { setLight(false); cmd("light", "off", "Light OFF"); }}>OFF</Btn>
        </Group>

        {/* Light Hub */}
        <Group icon={Radio} title="Light Hub" badge={ir ? "ON" : "OFF"}>
          <Btn active={ir}  testid="ir-on"  onClick={() => { setIr(true);  cmd("ir", "on",  "Light hub ON");  }}>ON</Btn>
          <Btn active={!ir} testid="ir-off" onClick={() => { setIr(false); cmd("ir", "off", "Light hub OFF"); }}>OFF</Btn>
        </Group>

        {/* Buzzer */}
        <Group icon={Bell} title="Buzzer Beeps" badge="0 = stop">
          <Btn key={0} testid="buzzer-0" onClick={() => cmd("buzzer", "0", "Buzzer STOP")}>
            STOP
          </Btn>
          {[1, 2, 3, 4, 5].map((n) => (
            <Btn key={n} testid={`buzzer-${n}`} onClick={() => cmd("buzzer", String(n), `Buzzer ×${n}`)}>
              {n}×
            </Btn>
          ))}
        </Group>

        {/* Alert Mute */}
        <Group icon={muted ? VolumeX : Volume2} title="Alert Sounds" badge={muted ? "MUTED" : "ACTIVE"}>
          <Btn
            active={!muted}
            testid="alerts-unmute"
            onClick={() => { setMuted(false); cmd("alerts", "unmute", "Alerts Unmuted"); }}
          >
            <Volume2 size={14} className="inline mr-1" /> UNMUTE
          </Btn>
          <Btn
            active={muted}
            testid="alerts-mute"
            onClick={() => { setMuted(true); cmd("alerts", "mute", "Alerts Muted"); }}
          >
            <VolumeX size={14} className="inline mr-1" /> MUTE
          </Btn>
        </Group>

        {/* Camera Snapshot */}
        <Group icon={Camera} title="Camera Snapshot">
          <Btn
            testid="cam-snapshot"
            onClick={() => sendCamCommand("snapshot")}
          >
            <Camera size={14} className="inline mr-1" /> Trigger Snapshot
          </Btn>
          <p className="w-full text-xs text-[var(--t2)] font-mono mt-1">
            Publishes to <span className="text-[var(--accent)]">campus/camera/cmd</span> - same as ST/REPT key.
          </p>
        </Group>

      </div>

      {/* Emergency Stop */}
      <Panel className="p-6">
        <Overline>Emergency Stop</Overline>
        <button
          data-testid="emergency-all-off-btn"
          onClick={() => {
            setFan("off"); setLight(false); setIr(false);
            cmd("system", "all_off", "ALL OFF - Emergency");
          }}
          className="emergency-pulse mt-4 w-full py-6 rounded-lg bg-[var(--danger)] text-white font-heading font-black text-xl sm:text-2xl uppercase tracking-[0.25em] flex items-center justify-center gap-3 transition-transform active:scale-[0.99] hover:brightness-110"
        >
          <ShieldOff size={26} strokeWidth={3} /> ALL OFF
        </button>
        <p className="text-xs text-[var(--t2)] mt-2 font-mono text-center">
          Cuts fan, lights, light hub and disarms outputs immediately.
        </p>
      </Panel>

      {/* IR Remote Reference */}
      <Panel className="p-6">
        <Overline>IR Remote Reference</Overline>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm" data-testid="ir-reference-table">
            <thead>
              <tr className="text-left text-[var(--t2)] border-b border-[var(--bd)]">
                <th className="py-2 pr-6 font-mono uppercase text-xs tracking-wider">Remote Key</th>
                <th className="py-2 font-mono uppercase text-xs tracking-wider">Action</th>
              </tr>
            </thead>
            <tbody>
              {IR_KEYS.map(([k, a]) => (
                <tr key={k} className="border-b border-[var(--bd)] hover:bg-[var(--accent-soft)] transition-colors">
                  <td className="py-2.5 pr-6 font-mono font-medium text-[var(--accent)]">{k}</td>
                  <td className="py-2.5 text-[var(--t2)]">{a}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
}
